import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Flame } from "lucide-react";
import { useLang } from "@/lib/i18n";

const LangToggle = ({ className = "" }) => {
    const { lang, setLang } = useLang();
    return (
        <div className={`flex items-center rounded-full border border-white/15 overflow-hidden ${className}`} data-testid="lang-toggle">
            {["fr", "en"].map((l) => (
                <button
                    key={l}
                    data-testid={`lang-${l}`}
                    onClick={() => setLang(l)}
                    className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors duration-300 ${
                        lang === l ? "bg-terracotta text-white" : "text-sand hover:text-cream"
                    }`}
                >
                    {l}
                </button>
            ))}
        </div>
    );
};

const Navbar = () => {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const { tr } = useLang();

    const LINKS = [
        { to: "/", label: tr("Accueil", "Home"), id: "accueil" },
        { to: "/programmes", label: tr("Programmes", "Programs"), id: "programmes" },
        { to: "/ebook", label: "Ebook", id: "ebook" },
        { to: "/temoignages", label: tr("Témoignages", "Testimonials"), id: "temoignages" },
        { to: "/a-propos", label: tr("À propos", "About"), id: "a-propos" },
        { to: "/faq", label: "FAQ", id: "faq" },
    ];

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header className="fixed top-0 inset-x-0 z-40 border-b border-white/10 bg-black/60 backdrop-blur-xl">
            <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between gap-4">
                <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group shrink-0">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-terracotta text-white transition-transform duration-300 group-hover:rotate-12">
                        <Flame className="h-5 w-5" strokeWidth={2.4} />
                    </span>
                    <span className="font-heading font-extrabold text-lg md:text-xl tracking-tight uppercase">
                        Coach <span className="text-terracotta">Roméo</span>
                    </span>
                </Link>

                <nav className="hidden lg:flex items-center gap-8" data-testid="nav-desktop">
                    {LINKS.map((l) =>
                        l.to === "/ebook" ? (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid="nav-link-ebook"
                                className={({ isActive }) =>
                                    `inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm uppercase tracking-[0.14em] font-bold transition-colors duration-300 ${
                                        isActive
                                            ? "border-ochre bg-ochre text-obsidian"
                                            : "border-ochre/60 text-ochre hover:bg-ochre hover:text-obsidian"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ) : (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={`nav-link-${l.id}`}
                                className={({ isActive }) =>
                                    `text-sm uppercase tracking-[0.14em] font-medium transition-colors duration-300 ${
                                        isActive ? "text-terracotta" : "text-sand hover:text-cream"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        )
                    )}
                </nav>

                <div className="flex items-center gap-3 shrink-0">
                    <LangToggle className="hidden sm:flex" />
                    <Link
                        to="/contact"
                        data-testid="nav-cta-consultation"
                        className="hidden md:inline-flex items-center rounded-full bg-terracotta px-6 py-2.5 text-sm font-bold uppercase tracking-wider text-white transition-colors duration-300 hover:bg-terracotta-hover"
                    >
                        {tr("Consultation", "Consultation")}
                    </Link>
                    <button
                        data-testid="nav-menu-toggle"
                        onClick={() => setOpen((v) => !v)}
                        aria-label="Menu"
                        className="lg:hidden flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-cream"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        data-testid="nav-mobile"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:hidden overflow-hidden border-t border-white/10 bg-obsidian/95 backdrop-blur-xl"
                    >
                        <div className="px-6 py-6 flex flex-col gap-1">
                            {LINKS.map((l, i) => (
                                <motion.div
                                    key={l.to}
                                    initial={{ opacity: 0, x: -16 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.05 * i }}
                                >
                                    <NavLink
                                        to={l.to}
                                        data-testid={`nav-mobile-link-${l.id}`}
                                        className={({ isActive }) =>
                                            `block py-3 font-heading text-2xl font-bold uppercase tracking-tight ${
                                                l.to === "/ebook" || isActive ? (l.to === "/ebook" ? "text-ochre" : "text-terracotta") : "text-cream"
                                            }`
                                        }
                                    >
                                        {l.label}
                                        {l.to === "/ebook" && (
                                            <span className="ml-3 align-middle rounded-full bg-ochre px-3 py-1 text-[10px] font-bold tracking-widest text-obsidian">
                                                39,99 $
                                            </span>
                                        )}
                                    </NavLink>
                                </motion.div>
                            ))}
                            <div className="mt-4 flex items-center gap-4">
                                <Link
                                    to="/contact"
                                    data-testid="nav-mobile-cta-consultation"
                                    className="flex-1 inline-flex items-center justify-center rounded-full bg-terracotta px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-white"
                                >
                                    {tr("Réserver une consultation", "Book a consultation")}
                                </Link>
                                <LangToggle />
                            </div>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Navbar;
